/**
 * BoardSkeleton - Loading state while Y.Doc syncs
 *
 * Per CONTEXT.md: Full-page skeleton/shimmer while Y.Doc syncs on board load.
 * Shows animated placeholders that mimic board content structure.
 */

interface BoardSkeletonProps {
  // Optional message to display
  message?: string;
}

export function BoardSkeleton({ message = 'Loading board...' }: BoardSkeletonProps) {
  return (
    <div className="flex h-full w-full flex-col bg-gray-50">
      {/* Header skeleton */}
      <div className="flex h-14 items-center justify-between border-b bg-white px-4">
        {/* Logo placeholder */}
        <div className="h-8 w-32 animate-pulse rounded bg-gray-200" />

        {/* Avatar stack placeholder */}
        <div className="flex -space-x-2">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
        </div>
      </div>

      {/* Canvas area skeleton */}
      <div className="flex-1 p-8">
        <div className="grid h-full grid-cols-3 gap-6">
          {/* Placeholder cards mimicking sticky notes */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-lg bg-white p-4 shadow-sm"
              style={{
                animationDelay: `${i * 100}ms`,
              }}
            >
              <div className="mb-3 h-4 w-3/4 rounded bg-gray-200" />
              <div className="mb-2 h-3 w-full rounded bg-gray-100" />
              <div className="mb-2 h-3 w-5/6 rounded bg-gray-100" />
              <div className="h-3 w-2/3 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>

      {/* Loading message */}
      <div className="flex items-center justify-center gap-2 pb-8 text-sm text-gray-500">
        <svg
          className="h-4 w-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span>{message}</span>
      </div>
    </div>
  );
}
